// Mes Chantiers
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Hammer, CheckCircle, Clock, Calendar, Star, MessageCircle, FileText, ChevronRight, Loader2, ArrowLeft, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEMO_CHANTIERS = [
  {
    id: 'demo-c1',
    title: 'Réfection de toiture',
    artisan: 'Léonard Martin',
    artisan_id: 'artisan-1',
    status: 'completed',
    startDate: '12 janv. 2025',
    endDate: '28 janv. 2025',
    amount: 3200,
    paymentStatus: 'Payé',
    rated: true,
    myRating: 4,
    description: 'Remplacement des tuiles et isolation sous toiture',
  },
  {
    id: 'demo-c2',
    title: 'Pose parquet chêne 45m²',
    artisan: 'Pierre Moreau',
    artisan_id: 'artisan-2',
    status: 'inProgress',
    startDate: '3 juin 2025',
    endDate: null,
    amount: 2800,
    paymentStatus: 'Acompte versé',
    rated: false,
    description: 'Pose de parquet flottant chêne massif dans le salon et couloir',
  },
  {
    id: 'demo-c3',
    title: 'Installation borne de recharge',
    artisan: 'Jean-Paul Renard',
    artisan_id: 'artisan-3',
    status: 'planned',
    startDate: '20 juin 2025',
    endDate: null,
    amount: 850,
    paymentStatus: 'Non payé',
    rated: false,
    description: 'Installation borne IRVE 7kW en garage',
  },
];

function ChantierCard({ chantier, onContact, onReview }) {
  const statusConfig = {
    completed: { label: 'Terminé', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
    inProgress: { label: 'En cours', bg: 'bg-blue-100', text: 'text-[#1E3A5F]', icon: Clock },
    planned: { label: 'Planifié', bg: 'bg-amber-100', text: 'text-amber-700', icon: Calendar },
  };
  const status = statusConfig[chantier.status] || statusConfig.planned;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-orange-200 hover:shadow-md transition-all"
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 truncate">{chantier.title}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{chantier.artisan}</p>
          </div>
          <span className={`ml-2 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 flex-shrink-0 ${status.bg} ${status.text}`}>
            <StatusIcon size={12} />
            {status.label}
          </span>
        </div>

        {chantier.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{chantier.description}</p>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Date début</p>
            <p className="text-sm font-medium text-gray-900">{chantier.startDate}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Date fin</p>
            <p className="text-sm font-medium text-gray-900">{chantier.endDate || 'En cours…'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Montant</p>
            <p className="text-sm font-bold text-[#1E3A5F]">{Number(chantier.amount).toLocaleString('fr-FR')} €</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Paiement</p>
            <p className="text-sm font-medium text-gray-900">{chantier.paymentStatus}</p>
          </div>
        </div>

        {chantier.status === 'completed' && !chantier.rated && (
          <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 mb-4">
            <p className="text-sm text-amber-800 font-medium">
              ⭐ N'oublie pas de laisser un avis pour {chantier.artisan} !
            </p>
          </div>
        )}

        {chantier.rated && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-gray-500 font-medium">Ton avis :</span>
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14}
                  className={i < chantier.myRating ? 'text-amber-500 fill-amber-500' : 'text-gray-200'} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2">
        <button
          onClick={() => onContact(chantier, 'documents')}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-700 hover:bg-white hover:border-orange-200 hover:text-orange-600 transition-all"
        >
          <FileText size={14} /> Documents
        </button>
        <button
          onClick={() => onContact(chantier, 'message')}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-700 hover:bg-white hover:border-[#1E3A5F] hover:text-[#1E3A5F] transition-all"
        >
          <MessageCircle size={14} /> Contacter
        </button>
        {chantier.status === 'completed' && !chantier.rated && (
          <button
            onClick={() => onReview(chantier)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-white transition-all shadow-sm"
          >
            <Star size={14} /> Avis
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default function ChantiersPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [chantiers, setChantiers] = useState(DEMO_CHANTIERS);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(true);

  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    fetchChantiers();
  }, []);

  async function fetchChantiers() {
    setLoading(true);
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token') || '';
      const res = await fetch(`${API_URL}/api/artisans/my-chantiers`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        const real = data.chantiers || [];
        if (real.length > 0) {
          setChantiers(real);
          setIsDemo(false);
        }
      }
    } catch (e) {
      console.error('fetchChantiers error', e);
    } finally {
      setLoading(false);
    }
  }

  function handleContact(chantier, type) {
    if (type === 'documents') {
      toast.info('Section Documents bientôt disponible');
    } else {
      navigate(`/cockpit-particulier/messagerie?to=${chantier.artisan_id}&name=${encodeURIComponent(chantier.artisan)}`);
    }
  }

  function handleReview(chantier) {
    navigate('/cockpit-particulier/artisans/avis');
  }

  const filters = [
    { id: 'all', label: 'Tous', icon: null },
    { id: 'inProgress', label: 'En cours', icon: Clock },
    { id: 'completed', label: 'Terminés', icon: CheckCircle },
    { id: 'planned', label: 'Planifiés', icon: Calendar },
  ];

  const filterCounts = {
    all: chantiers.length,
    inProgress: chantiers.filter(c => c.status === 'inProgress').length,
    completed: chantiers.filter(c => c.status === 'completed').length,
    planned: chantiers.filter(c => c.status === 'planned').length,
  };

  const filteredChantiers = chantiers.filter(c => filter === 'all' || c.status === filter);
  const totalAmount = chantiers.reduce((acc, c) => acc + (Number(c.amount) || 0), 0);

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Chantiers</h1>
          <p className="text-sm text-gray-500">
            {loading ? '…' : `${chantiers.length} chantier${chantiers.length > 1 ? 's' : ''} • Total : ${totalAmount.toLocaleString('fr-FR')} €`}
          </p>
        </div>
        {isDemo && (
          <span className="px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-lg text-xs font-medium text-amber-700">
            Aperçu démo
          </span>
        )}
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-4 border border-gray-200 text-center hover:border-[#1E3A5F]/30 transition-colors">
          <p className="text-2xl font-bold text-[#1E3A5F]">{chantiers.filter(c => c.status === 'inProgress').length}</p>
          <p className="text-xs text-gray-500 mt-0.5">En cours</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 text-center hover:border-green-300 transition-colors">
          <p className="text-2xl font-bold text-green-600">{chantiers.filter(c => c.status === 'completed').length}</p>
          <p className="text-xs text-gray-500 mt-0.5">Terminés</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 text-center hover:border-amber-300 transition-colors">
          <p className="text-2xl font-bold text-amber-600">{chantiers.filter(c => c.status === 'planned').length}</p>
          <p className="text-xs text-gray-500 mt-0.5">Planifiés</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-3">
        <div className="sm:hidden">
          <div className="relative flex items-center">
            <div ref={scrollContainerRef} onScroll={handleScroll}
              className="flex gap-1.5 overflow-x-auto flex-1 pr-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {filters.map(f => {
                const count = filterCounts[f.id] || 0;
                const FilterIcon = f.icon;
                return (
                  <button key={f.id} onClick={() => setFilter(f.id)}
                    className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                      filter === f.id ? 'bg-orange-500 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}>
                    {FilterIcon && <FilterIcon size={12} />}
                    {f.label}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${filter === f.id ? 'bg-white/20' : 'bg-gray-100'}`}>{count}</span>
                  </button>
                );
              })}
            </div>
            {showScrollHint && (
              <div className="flex-shrink-0 flex items-center pointer-events-none">
                <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
              </div>
            )}
          </div>
        </div>
        <div className="hidden sm:flex flex-wrap gap-2">
          {filters.map(f => {
            const count = filterCounts[f.id] || 0;
            const FilterIcon = f.icon;
            return (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                  filter === f.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}>
                {FilterIcon && <FilterIcon size={14} />}
                {f.label}
                <span className={`px-1.5 py-0.5 rounded text-xs ${filter === f.id ? 'bg-white/20' : 'bg-gray-200'}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="animate-spin text-orange-500" />
        </div>
      ) : filteredChantiers.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Hammer size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun chantier</h3>
          <p className="text-gray-500 text-sm">Tu n'as pas encore de chantier dans cette catégorie.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredChantiers.map(chantier => (
            <ChantierCard key={chantier.id} chantier={chantier}
              onContact={handleContact} onReview={handleReview} />
          ))}
        </div>
      )}
    </div>
  );
}
